/**
 * Map label glyphs rendered in the browser -- no font server, no font files.
 *
 * MapLibre draws text from SDF glyph ranges (`{fontstack}/{range}.pbf`) that
 * normally come from a glyph server. The style points `glyphs` at the
 * `localglyphs://` protocol registered here, which draws each requested
 * range with TinySDF on a canvas in the app's own typeface (Public Sans)
 * and encodes it as the same protobuf a glyph server returns. Ranges are
 * 256 characters and only requested when a label needs them.
 *
 * Use font stacks like `["Public Sans Semibold"]`; the weight is read from
 * the stack name (Regular, Medium, Semibold, Bold).
 */
import maplibregl from 'maplibre-gl';
import TinySDF from '@mapbox/tiny-sdf';

export const GLYPHS_URL = 'localglyphs://{fontstack}/{range}';
export const LABEL_FONT = ['Public Sans Semibold'];

const FONT_FAMILY = '"Public Sans", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
// Must match MapLibre's server-glyph conventions (24px SDF, 3px border).
const FONT_SIZE = 24;
const BUFFER = 3;
const RADIUS = 8;
// MapLibre aligns locally drawn glyphs with server glyphs using these.
const TOP_ADJUSTMENT = 27.5;

function weightOf(stack: string): string {
  if (/bold/i.test(stack) && !/semi/i.test(stack)) return '700';
  if (/semibold|semi bold/i.test(stack)) return '600';
  if (/medium/i.test(stack)) return '500';
  return '400';
}

class PbfWriter {
  private parts: number[] = [];

  varint(value: number): this {
    let v = value >>> 0;
    while (v > 127) {
      this.parts.push((v & 127) | 128);
      v >>>= 7;
    }
    this.parts.push(v);
    return this;
  }

  /** Field header + varint value. */
  uint(field: number, value: number): this {
    return this.varint(field << 3).varint(value);
  }

  sint(field: number, value: number): this {
    return this.uint(field, (value << 1) ^ (value >> 31));
  }

  bytes(field: number, data: ArrayLike<number>): this {
    this.varint((field << 3) | 2).varint(data.length);
    for (let i = 0; i < data.length; i += 1) this.parts.push(data[i] ?? 0);
    return this;
  }

  string(field: number, text: string): this {
    return this.bytes(field, new TextEncoder().encode(text));
  }

  finish(): Uint8Array {
    return Uint8Array.from(this.parts);
  }
}

const sdfByStack = new Map<string, TinySDF>();

export function encodeGlyphRange(stack: string, start: number, end: number): Uint8Array {
  let sdf = sdfByStack.get(stack);
  if (!sdf) {
    sdf = new TinySDF({
      fontSize: FONT_SIZE,
      buffer: BUFFER,
      radius: RADIUS,
      cutoff: 0.25,
      fontFamily: FONT_FAMILY,
      fontWeight: weightOf(stack),
    });
    sdfByStack.set(stack, sdf);
  }
  const fontstack = new PbfWriter().string(1, stack).string(2, `${start}-${end}`);
  for (let id = start; id <= end; id += 1) {
    const glyph = sdf.draw(String.fromCharCode(id));
    const width = Math.max(0, glyph.width - 2 * BUFFER);
    const height = Math.max(0, glyph.height - 2 * BUFFER);
    const message = new PbfWriter()
      .uint(1, id)
      .bytes(2, glyph.data)
      .uint(3, width)
      .uint(4, height)
      .sint(5, Math.round(glyph.glyphLeft))
      .sint(6, Math.round(glyph.glyphTop - TOP_ADJUSTMENT))
      .uint(7, Math.round(glyph.glyphAdvance))
      .finish();
    fontstack.bytes(3, message);
  }
  return new PbfWriter().bytes(1, fontstack.finish()).finish();
}

let registered = false;

/** Register the `localglyphs://` protocol (idempotent). Call before creating a map. */
export function registerLocalGlyphs(): void {
  if (registered) return;
  registered = true;
  maplibregl.addProtocol('localglyphs', async (params) => {
    const match = /^localglyphs:\/\/([^/]+)\/(\d+)-(\d+)/.exec(params.url);
    if (!match) throw new Error(`Bad glyph URL: ${params.url}`);
    // A stack may list fallbacks ("A,B"); draw with the first.
    const stack = (decodeURIComponent(match[1] ?? '').split(',')[0] ?? '').trim();
    try {
      await document.fonts?.load(`${weightOf(stack)} ${FONT_SIZE}px "Public Sans"`);
    } catch {
      // Font not available yet -- the canvas falls back to the system sans.
    }
    const bytes = encodeGlyphRange(stack, Number(match[2]), Number(match[3]));
    return { data: bytes.buffer as ArrayBuffer };
  });
}
